import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { auth } from '../../helpers/Firebase';
import {
  LOGIN_USER,
  REGISTER_USER,
  LOGOUT_USER,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
} from '../actions';

import {
  loginUserSuccess,
  loginUserError,
  registerUserSuccess,
  registerUserError,
  forgotPasswordSuccess,
  forgotPasswordError,
  resetPasswordSuccess,
  resetPasswordError,
} from './actions';

import { adminRoot, currentUser } from '../../constants/defaultValues';
import { setCurrentUser } from '../../helpers/Utils';
import AuthAPI from '../../services/yellow-school/auth';
import Platforms from '../../constants/platforms';

const loginWithEmailPasswordAsync = async (email, password, platform) => {
  const loginRequest = {
    [Platforms.YellowSchool]: () =>
      AuthAPI.loginYellowSchool({ email, password })
        .then((res) => ({
          success: true,
          token: res?.data?.accessToken,
          refresh: res?.data?.refreshToken,
        }))
        .catch((error) => ({
          // Always return a failure shape. The Yellow School API returns
          // `{ status, error }` (no `success: false`), so treating the raw
          // error string as a successful login left users stuck on /login.
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Email or password is incorrect',
        })),
    [Platforms.ShanghaiBloggersCLub]: () =>
      AuthAPI.loginSBC({ email, password })
        .then((res) => ({
          success: true,
          token: res?.data?.access_token,
          refresh: res?.data?.refresh_token,
        }))
        .catch((err) => ({
          message:
            err?.response?.data?.message ?? 'Email or password is incorrect',
        })),
    [Platforms.UkrChat]: () =>
      AuthAPI.loginUkrChat({ email, password })
        .then((res) => ({
          success: true,
          token: res?.data?.access_token,
          refresh: res?.data?.refresh_token,
        }))
        .catch((err) => ({
          message:
            err?.response?.data?.message ?? 'Email or password is incorrect',
        })),
  };

  return loginRequest[platform]();
};

export function* watchLoginUser() {
  yield takeEvery(LOGIN_USER, loginWithEmailPassword);
}

function* loginWithEmailPassword({ payload }) {
  const { email, password, platform } = payload.user;
  const { history } = payload;

  try {
    const loginUser = yield call(
      loginWithEmailPasswordAsync,
      email,
      password,
      platform
    );

    if (loginUser?.success && loginUser?.token) {
      const item = {
        token: loginUser.token,
        refresh: loginUser.refresh,
        platform,
      };

      setCurrentUser(item);
      yield put(loginUserSuccess(item));

      // eslint-disable-next-line no-unused-expressions
      history?.push(adminRoot);
    } else {
      yield put(
        loginUserError(
          loginUser?.message || 'Email or password is incorrect'
        )
      );
    }
  } catch (error) {
    yield put(
      loginUserError(
        error?.message || error || 'Email or password is incorrect'
      )
    );
  }
}

const registerWithEmailPasswordAsync = async (email, password) =>
  // eslint-disable-next-line no-return-await
  await auth
    .createUserWithEmailAndPassword(email, password)
    .then((user) => user)
    .catch((error) => error);

function* registerWithEmailPassword({ payload }) {
  const { email, password } = payload.user;
  const { history } = payload;
  try {
    const registerUser = yield call(
      registerWithEmailPasswordAsync,
      email,
      password
    );
    if (!registerUser.message) {
      const item = { uid: registerUser.user.uid, ...currentUser };
      setCurrentUser(item);
      yield put(registerUserSuccess(item));
      history.push(adminRoot);
    } else {
      yield put(registerUserError(registerUser.message));
    }
  } catch (error) {
    yield put(registerUserError(error));
  }
}

export function* watchRegisterUser() {
  yield takeEvery(REGISTER_USER, registerWithEmailPassword);
}

export function* watchLogoutUser() {
  yield takeEvery(LOGOUT_USER, logout);
}

const logoutAsync = async (history) => {
  await auth
    .signOut()
    .then((user) => user)
    .catch((error) => error);
  // eslint-disable-next-line no-unused-expressions
  history?.push(adminRoot);
};

function* logout({ payload }) {
  const { history } = payload;
  setCurrentUser();
  yield call(logoutAsync, history);
}

const forgotPasswordAsync = async (email) => {
  // eslint-disable-next-line no-return-await
  return await auth
    .sendPasswordResetEmail(email)
    .then((user) => user)
    .catch((error) => error);
};

function* forgotPassword({ payload }) {
  const { email } = payload.forgotUserMail;
  try {
    const forgotPasswordStatus = yield call(forgotPasswordAsync, email);
    if (!forgotPasswordStatus) {
      yield put(forgotPasswordSuccess('success'));
    } else {
      yield put(forgotPasswordError(forgotPasswordStatus.message));
    }
  } catch (error) {
    yield put(forgotPasswordError(error));
  }
}
export function* watchForgotPassword() {
  yield takeEvery(FORGOT_PASSWORD, forgotPassword);
}

const resetPasswordAsync = async (resetPasswordCode, newPassword) => {
  // eslint-disable-next-line no-return-await
  return await auth
    .confirmPasswordReset(resetPasswordCode, newPassword)
    .then((user) => user)
    .catch((error) => error);
};

function* resetPassword({ payload }) {
  const { newPassword, resetPasswordCode } = payload;
  try {
    const resetPasswordStatus = yield call(
      resetPasswordAsync,
      resetPasswordCode,
      newPassword
    );
    if (!resetPasswordStatus) {
      yield put(resetPasswordSuccess('success'));
    } else {
      yield put(resetPasswordError(resetPasswordStatus.message));
    }
  } catch (error) {
    yield put(resetPasswordError(error));
  }
}

export function* watchResetPassword() {
  yield takeEvery(RESET_PASSWORD, resetPassword);
}

export default function* rootSaga() {
  yield all([
    fork(watchLoginUser),
    fork(watchLogoutUser),
    fork(watchRegisterUser),
    fork(watchForgotPassword),
    fork(watchResetPassword),
  ]);
}
